import { useCallback, useLayoutEffect, useState } from 'react';

type MaybeElement = HTMLElement | null;

export function isElementTruncated(element: MaybeElement): boolean {
  if (!element) {
    return false;
  }

  return (
    element.scrollWidth > element.clientWidth ||
    element.scrollHeight > element.clientHeight
  );
}

export type UseTruncationObserverOptions = {
  /**
   * When false, skips a per-instance `window` `resize` listener. Use in dense tables:
   * `ResizeObserver` on the element still runs when layout changes (including viewport resize).
   * @default true
   */
  subscribeWindowResize?: boolean;
};

export function useTruncationObserver(options?: UseTruncationObserverOptions): {
  truncationRef: (node: HTMLElement | null) => void;
  isTruncated: boolean;
  checkTruncation: () => void;
} {
  const subscribeWindowResize = options?.subscribeWindowResize ?? true;
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [isTruncated, setIsTruncated] = useState(false);

  const checkTruncation = useCallback(() => {
    setIsTruncated(isElementTruncated(element));
  }, [element]);

  const truncationRef = useCallback((node: HTMLElement | null) => {
    setElement(node);
  }, []);

  useLayoutEffect(() => {
    if (!element) {
      return undefined;
    }

    const update = (): void => {
      setIsTruncated(isElementTruncated(element));
    };

    update();
    const rafId = window.requestAnimationFrame(update);
    const timeoutId = window.setTimeout(update, 50);

    const resizeObserver =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => update())
        : null;
    resizeObserver?.observe(element);

    const mutationObserver = new MutationObserver(() => update());
    mutationObserver.observe(element, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    if (subscribeWindowResize) {
      window.addEventListener('resize', update);
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      resizeObserver?.disconnect();
      mutationObserver.disconnect();
      if (subscribeWindowResize) {
        window.removeEventListener('resize', update);
      }
    };
  }, [element, subscribeWindowResize]);

  return { truncationRef, isTruncated, checkTruncation };
}
